import React, { useState, useEffect, useRef } from "react";
import "./search.css";
import Navbar from "../../components/navbar/navbar";
import Footer from "../../components/footer/Footer";
import { useTranslation } from "react-i18next";
import { FaSearch, FaBox, FaExclamationCircle, FaFileAlt, FaPlane, FaCube, FaHeadset, FaTimes, FaChevronRight } from "react-icons/fa";
import Helmet from "react-helmet";
import { searchData } from "./searchData";
import { Link, useNavigate } from "react-router-dom";

function SearchView() {
    const { t, i18n } = useTranslation("search");
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const navigate = useNavigate();
    const dropdownRef = useRef(null);

    // Map i18n language to searchData keys (ensure 'en-US' or 'en' maps to 'us')
    const langKey = i18n.language?.startsWith("es") ? "es" : "us";

    const [selectedCategory, setSelectedCategory] = useState("All");

    // Close suggestions when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const highlightText = (text, highlight) => {
        if (!text || !highlight.trim()) return text || "";
        const parts = text.split(new RegExp(`(${highlight})`, "gi"));
        return (
            <span>
                {parts.map((part, i) =>
                    part.toLowerCase() === highlight.toLowerCase() ? (
                        <mark key={i} className="search-highlight">{part}</mark>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);

        if (value.trim().length > 1) {
            const filteredSuggestions = searchData.filter(item =>
                item.title[langKey]?.toLowerCase().includes(value.toLowerCase()) ||
                item.tags[langKey]?.some(tag => tag?.toLowerCase().includes(value.toLowerCase()))
            ).slice(0, 5); 

            setSuggestions(filteredSuggestions);
            setShowSuggestions(filteredSuggestions.length > 0);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        if (!query.trim()) return;

        const filtered = searchData.filter(item =>
            item.title[langKey]?.toLowerCase().includes(query.toLowerCase()) ||
            item.description[langKey]?.toLowerCase().includes(query.toLowerCase()) ||
            item.tags[langKey]?.some(tag => tag?.toLowerCase().includes(query.toLowerCase()))
        );

        // Group by category
        const grouped = filtered.reduce((acc, item) => {
            const cat = item.category[langKey] || (langKey === "es" ? "General" : "General");
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(item);
            return acc;
        }, {});

        setResults(grouped);
        setHasSearched(true);
        setShowSuggestions(false);
        setSelectedCategory("All");
    };

    const handleSuggestionClick = (link) => {
        navigate(link);
    };

    const clearSearch = () => {
        setQuery("");
        setResults({});
        setHasSearched(false);
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedCategory("All");
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const cards = [
        {
            icon: <FaExclamationCircle />,
            title: t("cards.shipping_restrictions"),
            colorClass: "icon-pink",
            link: "/envios/restricciones"
        },
        {
            icon: <FaBox />,
            title: t("cards.tracking_guide"),
            colorClass: "icon-blue",
            link: "/seguimiento"
        },
        {
            icon: <FaFileAlt />,
            title: t("cards.billing_label"),
            colorClass: "icon-navy",
            link: "/contacto"
        },
        {
            icon: <FaPlane />,
            title: t("cards.quote_label"),
            colorClass: "icon-pink",
            link: "/cotizar"
        },
        {
            icon: <FaCube />,
            title: t("cards.products_services"),
            colorClass: "icon-blue",
            link: "/servicios/nacionales"
        },
        {
            icon: <FaHeadset />,
            title: t("cards.help_support"),
            colorClass: "icon-navy",
            link: "/faqs"
        }
    ];

    const allCategories = ["All", ...new Set(Object.keys(results))];
    const filteredResults = selectedCategory === "All"
        ? results
        : { [selectedCategory]: results[selectedCategory] };

    const totalResults = Object.values(results).flat().length;

    return (
        <div className="search-view-container">
            <Helmet>
                <title>{t("title")} | Aeromexico Cargo</title>
            </Helmet>
            <Navbar />

            <header className="search-header">
                <div className="search-main-icon-container">
                    <FaSearch />
                </div>
                <h2>{t("search_title")}</h2>
                <p>{t("search_subtitle")}</p>
                <div className="search-input-container" ref={dropdownRef}>
                    <input
                        type="text"
                        placeholder={t("placeholder")}
                        className="search-input"
                        value={query}
                        onChange={handleInputChange}
                        onFocus={() => query.length > 1 && suggestions.length > 0 && setShowSuggestions(true)}
                        onKeyPress={handleKeyPress}
                        autoComplete="off"
                    />
                    {query && (
                        <button className="search-clear-btn" onClick={clearSearch}>
                            <FaTimes />
                        </button>
                    )}
                    <button className="search-icon-btn" onClick={handleSearch}>
                        <FaSearch />
                    </button>

                    {/* Suggestions Dropdown */}
                    {showSuggestions && (
                        <div className="search-suggestions-dropdown">
                            {suggestions.map(suggestion => (
                                <Link
                                    to={suggestion.link[langKey]}
                                    key={suggestion.id}
                                    className="suggestion-item"
                                    onClick={() => setShowSuggestions(false)}
                                >
                                    <div className="suggestion-item-icon">
                                        {suggestion.icon}
                                    </div>
                                    <div className="suggestion-item-info">
                                        <span className="suggestion-item-title">{highlightText(suggestion.title[langKey], query)}</span>
                                        <span className="suggestion-item-category">{suggestion.category[langKey]}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </header>

            <main className="search-content">
                {hasSearched ? (
                    <div className="results-container">
                        <p className="results-summary">
                            {t("results.summary", {
                                query: query,
                                count: totalResults,
                                resultText: totalResults === 1 ? t("results.result_singular") : t("results.result_plural")
                            })}
                        </p>

                        {totalResults > 0 && (
                            <div className="category-filters">
                                        {allCategories.map(cat => (
                                            <button
                                                key={cat}
                                                className={`filter-btn ${selectedCategory === cat ? "active" : ""}`}
                                                onClick={() => setSelectedCategory(cat)}
                                            >
                                                {cat === "All" ? (langKey === "es" ? "Todos" : "All") : cat}
                                            </button>
                                        ))}
                            </div>
                        )}

                        <div className="results-list">
                            {Object.entries(filteredResults).map(([category, items]) => (
                                <div key={category} className="category-group">
                                    <h2 className="category-header">{category}</h2>
                                    <div className="category-items">
                                        {items.map(result => (
                                            <Link to={result.link[langKey]} key={result.id} className="result-item">
                                                <div className="result-icon">
                                                    {result.icon}
                                                </div>
                                                <div className="result-info">
                                                    <h3>{highlightText(result.title[langKey], query)}</h3>
                                                    <p>{highlightText(result.description[langKey], query)}</p>
                                                    <span className="result-link">
                                                        {t("results.see_more")} <FaChevronRight size={10} />
                                                    </span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="search-suggestions-container">
                            <h2>{t("not_found.title")}</h2>
                            <p>{t("not_found.subtitle")}</p>
                            <div className="suggestion-buttons">
                                <Link to="/servicios/nacionales" className="suggestion-btn">{t("not_found.categories.products")}</Link>
                                <Link to="/servicios/internacional" className="suggestion-btn">{t("not_found.categories.services")}</Link>
                                <Link to="/faqs" className="suggestion-btn">{t("not_found.categories.help")}</Link>
                                <Link to="/cumplimiento-normativo" className="suggestion-btn">{t("not_found.categories.compliance")}</Link>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>

                        <div className="search-grid">
                            {cards.map((card, index) => (
                                <Link to={card.link} key={index} className="search-card">
                                    <div className={`card-icon-container ${card.colorClass}`}>
                                        {card.icon}
                                    </div>
                                    <h3>{card.title}</h3>
                                </Link>
                            ))}
                        </div>
                    </>
                )}
            </main>

            <Footer />
        </div>
    );
}

export default SearchView;
